import { useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'

import { Input } from '@/components/ui/Input'
import { Avatar } from '@/components/ui/Avatar'
import { CaseStatusPill, QuoteStatusPill } from '@/components/ui/StatusPill'
import {
  labelKey,
  type RecordLabel,
  type RecordLabels,
} from '@/features/chat/lib/recordLabel'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { queryKeys } from '@/lib/query'
import type { ChatMessage, ChatSubjectKind, Conversation } from '@/types/domain'

/* The conversation list beside the thread (0029, 0052).
 *
 * Lifted out of ChatPage when the row grew from two lines to five. What it
 * renders per conversation is deliberately more than the list ENDPOINT sends:
 * the wire carries a subject pair, a timestamp and an unread count and nothing
 * human, so the record's number, its client and its lifecycle state are all
 * joined in on the client from the two lists the reader can already see. See
 * ChatPage for why that join is two cached list reads rather than a fetch per
 * row.
 */

/** A counter that moves whenever any chat THREAD moves in the query cache.
 *
 *  This exists so the previews below can re-render when a thread lands, and it
 *  is a raw cache subscription rather than the obvious `useQueries` over the
 *  same keys. A second observer on a thread's key would carry its own
 *  `queryFn` onto the shared query — and the next `invalidateQueries` would
 *  run whichever `queryFn` was written last, so a loaded conversation could be
 *  replaced by the empty array a preview-only observer would have supplied.
 *  Reading the cache never writes to it, and that is the whole reason for the
 *  shape of this hook.
 *
 *  Scoped to `['chat', 'thread', …]` rather than every event the cache emits:
 *  a subscription that woke on the case list's refetch would re-render this
 *  list for something it does not show. */
function useThreadCacheVersion(): number {
  const client = useQueryClient()
  const version = useRef(0)

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      client.getQueryCache().subscribe((event) => {
        const key = event.query.queryKey
        if (key[0] !== 'chat' || key[1] !== 'thread') return
        version.current += 1
        onStoreChange()
      }),
    [client],
  )

  /* Both snapshots return the ref, which is what useSyncExternalStore requires:
     a value that is referentially stable between notifications. Computing the
     previews here instead would hand back a fresh Map on every read and loop. */
  const read = useCallback(() => version.current, [])
  return useSyncExternalStore(subscribe, read, read)
}

/** The last line of every conversation THE CLIENT ALREADY HOLDS.
 *
 *  Worth being explicit about the limit, because the reference design shows a
 *  preview on every row: `/chat/conversations` does not carry one. The wire is
 *  id, kind, subject, archived, lastMessageAt, unread — there is no last
 *  message on it, and putting one there is a SERVER change (collab's
 *  ConversationDTO plus the query behind it), which is out of scope here.
 *
 *  So the preview is drawn from what the client happens to have: the thread of
 *  every conversation this reader has opened, which `useConversation` has
 *  already cached under the same key. A row whose thread has never been loaded
 *  renders no preview rather than a placeholder — an em dash where a sentence
 *  belongs reads as "nothing was said", which is a different and wrong fact.
 *
 *  Deliberately NOT a fetch per row. Twenty conversations would be twenty
 *  requests to render a list, which is the shape NFR-2 rules out. */
function useLastMessages(rows: Conversation[]): Map<string, ChatMessage> {
  const client = useQueryClient()
  const version = useThreadCacheVersion()

  return useMemo(() => {
    const byConversation = new Map<string, ChatMessage>()
    for (const row of rows) {
      const held = client.getQueryData<ChatMessage[]>(
        queryKeys.chat.thread(row.subjectKind, row.subjectId),
      )
      if (!held) continue
      /* Backwards to the first message still standing. A tombstone is the last
         thing in the thread whenever somebody deletes their own most recent
         message, and "Merdan: Message deleted" is a preview that tells the
         reader nothing about the conversation. */
      for (let i = held.length - 1; i >= 0; i -= 1) {
        const message = held[i]
        if (message && !message.deleted) {
          byConversation.set(row.id, message)
          break
        }
      }
    }
    return byConversation
    /* `version` is the dependency that matters and the one the linter cannot
       see: it changes precisely when a thread arrives in the cache. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, client, version])
}

export interface ConversationListProps {
  rows: Conversation[]
  labels: RecordLabels
  selectedId: string | null
  /** Which tab this list belongs to, for the search placeholder. The list is
   *  per tab, so offering to search cases on the quote tab would name rows that
   *  are not there. */
  kind: ChatSubjectKind
  emptyLabel: string
  onSelect: (subjectId: string) => void
}

export function ConversationList({
  rows,
  labels,
  selectedId,
  kind,
  emptyLabel,
  onSelect,
}: ConversationListProps) {
  const { t } = useTranslation()
  const { relative } = useFormat()
  const lastMessages = useLastMessages(rows)

  const [query, setQuery] = useState('')

  /* The filter, over rows ALREADY HELD. The server sends every conversation
     this reader may open in one response (there is no paging on that endpoint),
     so narrowing it is a substring test rather than a request — typing here
     must cost nothing on the wire.

     It matches the record NUMBER and the CLIENT NAME, which are the two things
     a person searching a list of shipments actually types. The id's head is
     matched too, because that is what an unresolved row renders as its title:
     a row must never be unreachable by the text it is showing. */
  const needle = query.trim().toLowerCase()
  const visible = useMemo(() => {
    if (!needle) return rows
    return rows.filter((row) => {
      const label = labels.get(labelKey(row.subjectKind, row.subjectId))
      const number = label?.number ?? row.subjectId.slice(0, 8)
      return (
        number.toLowerCase().includes(needle) ||
        (label?.clientName ?? '').toLowerCase().includes(needle)
      )
    })
  }, [rows, labels, needle])

  return (
    <div
      className={cn(
        'bg-surface border-line flex min-h-0 flex-col overflow-hidden',
        'rounded-[var(--radius-lg)] border',
      )}
    >
      {/* The search sits INSIDE the card and above the scroller, so it stays put
          while the rows move under it. A filter that scrolled away with the list
          would be a control the reader loses the moment it starts working. */}
      <div className="shrink-0 p-3">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery('')}
          iconLeft={Search}
          inputSize="md"
          /* The placeholder is not an accessible name — it disappears the
             instant somebody types, which is exactly when a screen reader user
             would want to be told what the box is. */
          aria-label={t('chat.search.label')}
          placeholder={
            kind === 'quote' ? t('chat.search.quotes') : t('chat.search.cases')
          }
        />
      </div>

      <ul className="asa-scroll min-h-0 flex-1 overflow-y-auto pb-2">
        {rows.length === 0 ? (
          <li className="text-text-muted px-3 py-4 text-[12.5px] font-light">
            {emptyLabel}
          </li>
        ) : visible.length === 0 ? (
          /* Distinct from `emptyLabel` above on purpose: this reader HAS
             conversations, the text they typed simply names none of them. */
          <li className="text-text-muted px-3 py-4 text-[12.5px] font-light">
            {t('chat.search.none')}
          </li>
        ) : (
          visible.map((row) => (
            <ConversationRow
              key={row.id}
              row={row}
              label={labels.get(labelKey(row.subjectKind, row.subjectId))}
              lastMessage={lastMessages.get(row.id)}
              active={row.subjectId === selectedId}
              relative={relative}
              onSelect={onSelect}
            />
          ))
        )}
      </ul>
    </div>
  )
}

function ConversationRow({
  row,
  label,
  lastMessage,
  active,
  relative,
  onSelect,
}: {
  row: Conversation
  label: RecordLabel | undefined
  lastMessage: ChatMessage | undefined
  active: boolean
  relative: (value: string | null | undefined) => string
  onSelect: (subjectId: string) => void
}) {
  const { t } = useTranslation()

  /* The record's own number, falling back to the id's head while the lists
     load — or when the conversation names a record this reader cannot see,
     which the server's scope makes impossible today but which a fallback costs
     nothing to survive. */
  const title = label?.number ?? row.subjectId.slice(0, 8)
  const client = label?.clientName ?? ''

  /* The chip only exists once the record has resolved. Rendering a neutral
     placeholder for the two-hundred milliseconds before the list lands would
     make every row flash a state the shipment is not in. */
  const status = label?.status
  const unread = row.unread > 0

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(row.subjectId)}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'relative flex w-full cursor-pointer gap-3 px-3 py-3 text-left',
          'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
          active ? 'bg-brand-soft' : 'hover:bg-surface-hover',
        )}
      >
        {/* The accent bar, not a border: a border on the selected row would
            change its width and shift every line inside it by three pixels as
            the reader moves down the list. */}
        {active ? (
          <span
            className="bg-brand absolute inset-y-0 left-0 w-[3px]"
            aria-hidden
          />
        ) : null}

        {/* Initials of the CLIENT, not of whoever spoke last. The row names a
            shipment, and the face beside it is the counterparty the reader is
            running it for — a face that changed with every message would stop
            being an identifier at all. */}
        <Avatar name={client || title} size="md" className="mt-0.5" />

        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex items-baseline gap-2">
            <span className="text-text-heading min-w-0 flex-1 truncate text-[13px] font-medium">
              {title}
            </span>
            {row.lastMessageAt ? (
              /* text-MUTED rather than text-faint, though the reference draws
                 this very quiet. When a conversation last moved appears nowhere
                 else on the row, so it is information rather than decoration
                 and may not sit at 2.77:1 (UX-DR-3). The hierarchy is carried
                 by the size instead. */
              <span className="text-text-muted shrink-0 text-[11px] font-light">
                {relative(row.lastMessageAt)}
              </span>
            ) : null}
          </span>

          {client ? (
            <span className="text-text-muted truncate text-[12px] font-light">
              {client}
            </span>
          ) : null}

          {status || row.archived || unread ? (
            <span className="flex items-center gap-2">
              {status?.kind === 'case' ? (
                <CaseStatusPill status={status.value} />
              ) : status?.kind === 'quote' ? (
                <QuoteStatusPill status={status.value} />
              ) : null}
              {row.archived ? (
                <span className="text-text-muted text-[11px] font-light">
                  {t('chat.archived')}
                </span>
              ) : null}
              <span className="min-w-0 flex-1" />
              {unread ? (
                <>
                  {/* A dot rather than a number, because the number is on the
                      row's own conversation the moment it opens and a two-digit
                      chip beside a status pill is two counts competing. The
                      count is still ANNOUNCED — a screen reader user gets the
                      figure a sighted one gets from the thread. */}
                  <span
                    className="bg-brand size-2 shrink-0 rounded-full"
                    aria-hidden
                  />
                  <span className="sr-only">
                    {row.unread >= 99
                      ? t('chat.unreadCapped')
                      : t('chat.unread', { count: row.unread })}
                  </span>
                </>
              ) : null}
            </span>
          ) : null}

          {lastMessage ? (
            <span className="text-text-muted truncate text-[12px] font-light">
              {t('chat.preview', {
                author: lastMessage.authorName,
                body: lastMessage.body,
              })}
            </span>
          ) : null}
        </span>
      </button>
    </li>
  )
}
